import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Eye, EyeOff, Search, Check, X, Upload, FileText, Image } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { bestPracticesAPI, adminAPI } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

const BP_CATEGORIES = [
    { id: 'fruits', name: 'Fruits' },
    { id: 'legumes', name: 'Légumes' },
    { id: 'plantes', name: 'Plantes & Fleurs' },
    { id: 'cosmetiques', name: 'Cosmétiques' },
    { id: 'general', name: 'Général' },
];

export const AdminBestPractices = () => {
    const [bestPractices, setBestPractices] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [selectedBP, setSelectedBP] = useState(null);
    const [searchClient, setSearchClient] = useState('');

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        category: 'general',
        target_client_ids: [],
        images: [],
        pdf_file: null,
    });

    const fetchData = async () => {
        try {
            const [bpRes, clientsRes] = await Promise.all([
                bestPracticesAPI.list(),
                adminAPI.getClients()
            ]);
            setBestPractices(bpRes.data.best_practices || []);
            setClients(clientsRes.data.clients || []);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const resetForm = () => {
        setFormData({ title: '', content: '', category: 'general', target_client_ids: [], images: [], pdf_file: null });
        setSelectedBP(null);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!formData.title || !formData.content) {
            toast.error('Titre et contenu sont obligatoires');
            return;
        }
        if (formData.target_client_ids.length === 0) {
            toast.error('Sélectionnez au moins un client');
            return;
        }

        try {
            const fd = new FormData();
            fd.append('title', formData.title);
            fd.append('content', formData.content);
            fd.append('category', formData.category);
            fd.append('target_client_ids', formData.target_client_ids.join(','));
            
            if (formData.images.length > 0) {
                formData.images.forEach(img => fd.append('images', img));
            }
            if (formData.pdf_file) {
                fd.append('pdf_file', formData.pdf_file);
            }

            await bestPracticesAPI.create(fd);
            toast.success('Guide publié avec succès');
            setIsCreateOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            console.error('Error:', error);
            toast.error('Erreur lors de la création');
        }
    };

    const handleDelete = async () => {
        if (!selectedBP) return;
        try {
            await bestPracticesAPI.delete(selectedBP.id);
            toast.success('Guide supprimé');
            setIsDeleteOpen(false);
            setSelectedBP(null);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleTogglePublish = async (bp) => {
        try {
            await bestPracticesAPI.update(bp.id, { is_published: !bp.is_published });
            toast.success(bp.is_published ? 'Guide masqué' : 'Guide publié');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const toggleClient = (clientId) => {
        setFormData(prev => ({
            ...prev,
            target_client_ids: prev.target_client_ids.includes(clientId)
                ? prev.target_client_ids.filter(id => id !== clientId)
                : [...prev.target_client_ids, clientId]
        }));
    };

    const selectAllClients = () => {
        setFormData(prev => ({
            ...prev,
            target_client_ids: clients.map(c => c.id)
        }));
    };

    const filteredClients = clients.filter(c =>
        c.company_name?.toLowerCase().includes(searchClient.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchClient.toLowerCase())
    );

    const getCategoryName = (catId) => BP_CATEGORIES.find(c => c.id === catId)?.name || catId;

    if (loading) {
        return (
            <div className="min-h-screen bg-sand flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jungle"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand" data-testid="admin-best-practices">
            <div className="max-w-6xl mx-auto p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-jungle">Bonnes Pratiques</h1>
                        <p className="text-muted-foreground text-sm">{bestPractices.length} guide(s) publiés</p>
                    </div>
                    <Button onClick={() => { resetForm(); setIsCreateOpen(true); }} className="bg-jungle hover:bg-jungle/90" data-testid="create-bp-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Nouveau guide
                    </Button>
                </div>

                {bestPractices.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <p className="text-muted-foreground">Aucun guide publié</p>
                            <p className="text-sm text-muted-foreground mt-1">Créez votre premier guide pour vos clients</p>
                        </CardContent>
                    </Card>
                ) : (
                    <Card>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Titre</TableHead>
                                    <TableHead>Catégorie</TableHead>
                                    <TableHead>Clients ciblés</TableHead>
                                    <TableHead>Pièces jointes</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Statut</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {bestPractices.map((bp) => (
                                    <TableRow key={bp.id}>
                                        <TableCell className="font-medium">{bp.title}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{getCategoryName(bp.category)}</Badge>
                                        </TableCell>
                                        <TableCell>{bp.target_client_ids?.length || 0} client(s)</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                {bp.image_urls?.length > 0 && (
                                                    <Badge variant="secondary" className="text-xs">
                                                        <Image className="h-3 w-3 mr-1" />{bp.image_urls.length}
                                                    </Badge>
                                                )}
                                                {bp.pdf_url && (
                                                    <Badge variant="secondary" className="text-xs">
                                                        <FileText className="h-3 w-3 mr-1" />PDF
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm">{formatDateTime(bp.created_at)}</TableCell>
                                        <TableCell>
                                            <Badge className={bp.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                                {bp.is_published ? 'Publié' : 'Masqué'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button variant="ghost" size="icon" onClick={() => handleTogglePublish(bp)} title={bp.is_published ? 'Masquer' : 'Publier'}>
                                                    {bp.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => { setSelectedBP(bp); setIsDeleteOpen(true); }} className="text-red-600">
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                )}
            </div>

            {/* Create Dialog */}
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Nouveau guide - Bonnes Pratiques</DialogTitle>
                        <DialogDescription>Publiez un guide pour vos clients sélectionnés</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div>
                            <Label htmlFor="bp-title">Titre *</Label>
                            <Input id="bp-title" value={formData.title} onChange={e => setFormData(p => ({...p, title: e.target.value}))} placeholder="Ex: Plan de mise en rayon - Fruits d'été" data-testid="bp-title-input" />
                        </div>

                        <div>
                            <Label htmlFor="bp-category">Catégorie</Label>
                            <Select value={formData.category} onValueChange={v => setFormData(p => ({...p, category: v}))}>
                                <SelectTrigger data-testid="bp-category-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {BP_CATEGORIES.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label htmlFor="bp-content">Contenu *</Label>
                            <Textarea id="bp-content" rows={8} value={formData.content} onChange={e => setFormData(p => ({...p, content: e.target.value}))} placeholder="Rédigez votre guide ici..." className="resize-y" data-testid="bp-content-input" />
                        </div>

                        <div>
                            <Label>Images (optionnel)</Label>
                            <div className="mt-1 border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                                <Input type="file" accept="image/*" multiple onChange={e => setFormData(p => ({...p, images: Array.from(e.target.files)}))} className="cursor-pointer" data-testid="bp-images-input" />
                                <p className="text-xs text-muted-foreground mt-1">JPG, PNG - Plusieurs images possibles</p>
                            </div>
                            {formData.images.length > 0 && (
                                <p className="text-sm text-green-600 mt-1">{formData.images.length} image(s) sélectionnée(s)</p>
                            )}
                        </div>

                        <div>
                            <Label>Document PDF (optionnel)</Label>
                            <div className="mt-1 border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                                <Input type="file" accept=".pdf" onChange={e => setFormData(p => ({...p, pdf_file: e.target.files[0] || null}))} className="cursor-pointer" data-testid="bp-pdf-input" />
                                <p className="text-xs text-muted-foreground mt-1">PDF uniquement</p>
                            </div>
                            {formData.pdf_file && (
                                <p className="text-sm text-green-600 mt-1">{formData.pdf_file.name}</p>
                            )}
                        </div>

                        {/* Client selection */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label>Clients ciblés * ({formData.target_client_ids.length} sélectionné(s))</Label>
                                <Button type="button" variant="outline" size="sm" onClick={selectAllClients}>
                                    <Check className="h-3 w-3 mr-1" />Tout sélectionner
                                </Button>
                            </div>
                            <Input placeholder="Rechercher un client..." value={searchClient} onChange={e => setSearchClient(e.target.value)} className="mb-2" />
                            <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                                {filteredClients.map(client => (
                                    <div key={client.id} className={`flex items-center gap-3 p-2 cursor-pointer hover:bg-gray-50 ${formData.target_client_ids.includes(client.id) ? 'bg-green-50' : ''}`} onClick={() => toggleClient(client.id)}>
                                        <div className={`w-5 h-5 rounded border flex items-center justify-center ${formData.target_client_ids.includes(client.id) ? 'bg-jungle border-jungle' : 'border-gray-300'}`}>
                                            {formData.target_client_ids.includes(client.id) && <Check className="h-3 w-3 text-white" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{client.company_name}</div>
                                            <div className="text-xs text-muted-foreground">{client.email}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Annuler</Button>
                            <Button type="submit" className="bg-jungle hover:bg-jungle/90" data-testid="bp-submit-btn">
                                <Upload className="h-4 w-4 mr-2" />Publier le guide
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Supprimer ce guide ?</DialogTitle>
                        <DialogDescription>
                            Le guide "{selectedBP?.title}" sera définitivement supprimé avec ses fichiers joints.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>Annuler</Button>
                        <Button variant="destructive" onClick={handleDelete} data-testid="bp-confirm-delete">Supprimer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
